import React from 'react';
import styled, { css } from 'styled-components';
import type {
  BreakpointOptions,
  BreakpointOptionsMap,
} from '@peloton/images/BreakpointTransforms';
import { gray2 } from '@engage/colors';
import { Image as SimpleImage } from './Image';
import { useBadgeFromSpriteSheet } from './member-score/useBadgeFromSpriteSheet';

const BADGE_SIZING_CONSTANT = 1 / 3;

const SimpleAvatar: React.FC<React.PropsWithChildren<SimpleAvatarProps>> = ({
  url: src,
  size,
  showBadge,
  isSelf,
  badgeUrl,
  ...props
}) => {
  const badgeSize = getSize(size) * BADGE_SIZING_CONSTANT;
  const badgeData = useBadgeFromSpriteSheet(showBadge ?? false, isSelf, badgeUrl);

  if (!src) {
    return <NoImage size={getSize(size)} {...props} />;
  }

  const imageProps = {
    breakpointOptions: toAvatarTransforms(size),
    src,
    className: 'fs-exclude',
    ...props,
    size: badgeSize,
    maskUrl: badgeData.maskUrl,
    avatarUrl: src,
    shouldShowMask: isSelf ?? false,
  };

  return badgeData.badgeUrl ? (
    <ImageContainer size={getSize(size)}>
      <StyledSimpleImage {...imageProps} />
      <BadgeIcon size={badgeSize} url={badgeData.badgeUrl} />
    </ImageContainer>
  ) : (
    <StyledSimpleImage {...imageProps} />
  );
};

const placeholderStyles = css`
  background-color: ${gray2};
  border-radius: 50%;
`;

const NoImage = styled.div<{ size: MaybeNumberOrBreakpoints }>`
  ${placeholderStyles}
  width: ${props => `${props.size}px`};
  height: ${props => `${props.size}px`};
`;

const StyledSimpleImage = styled(SimpleImage)<{
  size: number;
  maskUrl: string;
  avatarUrl: string;
  shouldShowMask: boolean;
}>`
  ${placeholderStyles}
  overflow: hidden;
  ${props =>
    props.shouldShowMask
      ? `mask-image: url(${props.avatarUrl}), url(${props.maskUrl});`
      : ''}
  mask-size: 100% 100%, ${props => `${props.size}px`};
  mask-position: 105% 105%;
  mask-repeat: no-repeat;
  mask-composite: subtract;
`;

const ImageContainer = styled.div<{ size: number }>`
  width: ${props => `${props.size}px`};
  height: ${props => `${props.size}px`};
  position: relative;
`;

const BadgeIcon = styled.div<{ size: number; url: string }>`
  position: absolute;
  right: -3%;
  bottom: -3%;
  width: ${props => `${props.size}px`};
  height: ${props => `${props.size}px`};
  background-image: url(${props => props.url});
  background-size: cover;
`;

const getSize = (size: MaybeNumberOrBreakpoints) => {
  if (typeof size === 'number') {
    return size;
  } else {
    return size?.[Object.keys(size)[0]]?.width;
  }
};

const toAvatarTransforms = (size: MaybeNumberOrBreakpoints): BreakpointOptions => {
  if (typeof size === 'number') {
    return {
      mobile: {
        aspectRatio: 1,
        gravity: 'face',
        height: size,
        width: size,
      },
    };
  } else {
    return size as BreakpointOptions;
  }
};

export type SimpleAvatarProps = React.HTMLAttributes<HTMLDivElement> & {
  showBadge?: boolean;
  size: MaybeNumberOrBreakpoints;
  alt: string;
  url?: string;
  loadingColor?: string;
  badgeUrl?: string;
  isSelf?: boolean;
};

type MaybeNumberOrBreakpoints = number | Partial<BreakpointOptionsMap>;

export { SimpleAvatar };
